/**
 * "Próximos passos" of the Início (HOME-07): the true pending items, in
 * order, at most 3, plus the chips of the conditions already satisfied. An
 * item whose data did not load (error or still loading) is left out.
 */
import type {
  TenantBillingStatusResponseDto,
  TenantDto,
} from '@/generated/korza-api-client/types.gen';
import type { AppFeatureState } from '@/lib/app-editor/app-hub';

export type NextStepId =
  | 'integrate'
  | 'billing'
  | 'welcome'
  | 'couponSetup'
  | 'couponOff';

/** `integrate` without `action` = unsupported platform (text only). */
export interface NextStep {
  id: NextStepId;
  href?: string;
  action?: 'integrationDialog';
}

export type DoneChip = 'integrated' | 'published' | 'billingOk';

export interface NextStepsInput {
  tenant: Pick<
    TenantDto,
    | 'slug'
    | 'isStoreIntegrationConfigured'
    | 'isAppUnderConstruction'
    | 'ecommercePlatform'
  >;
  /** `undefined` while the billing status is loading or failed. */
  billingState?: TenantBillingStatusResponseDto['state'];
  welcome: AppFeatureState;
  coupon: AppFeatureState;
}

const MAX_PENDING = 3;
const INTEGRATED_PLATFORMS = ['shopify', 'nuvemshop'];

// SPEC_DEVIATION: returns `total` too, and `welcome`/`coupon` are plain
// `AppFeatureState` (design: `AppFeatureState | 'error'`). Reason: the header
// counts every pending item ("04") while the list shows 3, and
// `AppFeatureState` already has `{ kind: 'error' }`.
export function getNextSteps(input: NextStepsInput): {
  pending: NextStep[];
  total: number;
  done: DoneChip[];
} {
  const { tenant, billingState, welcome, coupon } = input;
  const all: NextStep[] = [];

  if (!tenant.isStoreIntegrationConfigured && !tenant.isAppUnderConstruction) {
    all.push(
      INTEGRATED_PLATFORMS.includes(tenant.ecommercePlatform)
        ? { id: 'integrate', action: 'integrationDialog' }
        : { id: 'integrate' }
    );
  }
  if (billingState === 'warning') {
    all.push({ id: 'billing', href: `/${tenant.slug}/billing` });
  }
  if (
    welcome.kind === 'notConfigured' ||
    (welcome.kind === 'configured' && !welcome.active)
  ) {
    all.push({
      id: 'welcome',
      href: `/${tenant.slug}/customization/welcome-notification`,
    });
  }
  const couponHref = `/${tenant.slug}/customization/coupon-banner`;
  if (coupon.kind === 'notConfigured') {
    all.push({ id: 'couponSetup', href: couponHref });
  } else if (coupon.kind === 'configured' && !coupon.active) {
    all.push({ id: 'couponOff', href: couponHref });
  }

  const done: DoneChip[] = [];
  if (tenant.isStoreIntegrationConfigured) done.push('integrated');
  if (!tenant.isAppUnderConstruction) done.push('published');
  if (billingState === 'ok') done.push('billingOk');

  return { pending: all.slice(0, MAX_PENDING), total: all.length, done };
}
